import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import img1 from '../../img/img1.jpg';
import img2 from '../../img/img2.jpg';
import img3 from '../../img/img3.jpeg';
import img4 from '../../img/img4.jpeg';

export default function Home() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const heroImages = [
    img1,
    img2,
    img3,
    img4
  ];

  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000); // Change image every 5 seconds

      return () => clearInterval(interval);
    }
  }, [isHovered, heroImages.length]);

  // Preload images to ensure they load before carousel starts
  useEffect(() => {
    heroImages.forEach((image) => {
      const img = new Image();
      img.src = image;
      img.onload = () => console.log(`Image loaded: ${image}`);
      img.onerror = () => console.error(`Failed to load image: ${image}`);
    });
  }, []);

  const stats = [
    { number: '30+', label: 'Years of Excellence', icon: 'ri-calendar-line' },
    { number: '100,000+', label: 'MT Annually', icon: 'ri-truck-line' },
    { number: '10,000+', label: 'Partner Farmers', icon: 'ri-group-line' },
    { number: '15+', label: 'Countries Served', icon: 'ri-global-line' }
  ];

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: 'Trusted Legacy',
      description: 'Over 30 years of excellence in cocoa export since 1994'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainable Practices',
      description: 'Committed to environmental stewardship and farmer prosperity'
    },
    {
      icon: 'ri-award-line',
      title: 'Premium Quality',
      description: 'Rigorously checked sun-dried, machine-finished cocoa beans'
    },
    {
      icon: 'ri-earth-line',
      title: 'Global Compliance',
      description: 'EUDR and Rainforest Alliance certified supply chains'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section with Image Carousel */}
      <section 
        className="relative min-h-screen flex items-center overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Background Images Carousel */}
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${image}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        ))}

        {/* Animated background overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-green-900/20 to-amber-900/20 animate-pulse"></div>
        
        {/* Floating particles animation */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-amber-400/30 rounded-full animate-bounce delay-0"></div>
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-green-400/30 rounded-full animate-bounce delay-300"></div>
          <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-amber-300/30 rounded-full animate-bounce delay-700"></div>
          <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-white/40 rounded-full animate-pulse delay-1000"></div>
        </div>

        {/* Navigation Dots */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentImageIndex 
                  ? 'bg-amber-400 scale-125' 
                  : 'bg-white/50 hover:bg-white/80'
              }`}
            />
          ))}
        </div>

        <div className="container mx-auto px-4 w-full relative z-10">
          <div className="max-w-4xl animate-fade-in-up">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight transform transition-all duration-1000 hover:scale-105">
              Empowering the Cocoa Value Chain for a 
              <span className="text-amber-400 animate-pulse"> Sustainable Future</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl opacity-0 animate-fade-in-delayed">
              Premier Nigerian cocoa export company with a proud legacy established in 1994. 
              Delivering ethically sourced, traceable cocoa while advancing farmer prosperity and sustainable agriculture.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-late">
              <Link to="/services">
                <Button variant="primary" size="lg" className="w-full sm:w-auto transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
                  Explore Our Services
                  <div className="w-5 h-5 flex items-center justify-center ml-2">
                    <i className="ri-arrow-right-line animate-bounce"></i>
                  </div>
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" size="lg" className="w-full sm:w-auto bg-white/10 border-white text-white hover:bg-white hover:text-green-800 transform hover:scale-105 transition-all duration-300">
                  Learn About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-2xl text-amber-600`}></i>
                </div>
                <h3 className="text-3xl font-bold text-green-800 mb-2">{stat.number}</h3>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">
              Why Choose Olatunde International
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Leading Africa's cocoa export industry through sustainability, integrity, 
              and a commitment to empowering communities and protecting the environment.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-xl text-amber-600`}></i>
                </div>
                <h3 className="text-xl font-semibold text-green-800 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-800 mb-4">Our Core Services</h2>
            <p className="text-xl text-gray-600">Comprehensive cocoa solutions from farm to global markets</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 hover:shadow-2xl transition-all duration-500 group">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6 group-hover:animate-spin transition-transform duration-500">
                <i className="ri-plant-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-green-800 mb-4">Cocoa Sourcing & Export</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Working directly with farmers to deliver top-grade, sun-dried, machine-finished, 
                and hand-selected cocoa beans to global markets.
              </p>
              <Link to="/services">
                <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-600 transform hover:scale-105 transition-all duration-300">
                  Learn More
                </Button>
              </Link>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 hover:shadow-2xl transition-all duration-500 group">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6 group-hover:animate-spin transition-transform duration-500">
                <i className="ri-shield-check-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-green-800 mb-4">Traceability & Certification</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                GPS farm mapping, farmer ID systems, and monitoring tools ensuring 
                full compliance with global standards and regulations.
              </p>
              <Link to="/services">
                <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-600 transform hover:scale-105 transition-all duration-300">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.9), rgba(34, 197, 94, 0.9)), url('https://readdy.ai/api/search-image?query=Close-up%20of%20premium%20cocoa%20beans%20spread%20for%20drying%20under%20golden%20sunlight%2C%20rich%20brown%20cocoa%20beans%20with%20natural%20texture%2C%20traditional%20African%20drying%20methods%2C%20warm%20earth%20tones%2C%20agricultural%20excellence%2C%20sustainable%20farming%20practices%2C%20natural%20lighting%2C%20detailed%20texture%20photography&width=1920&height=600&seq=cocoa-beans-drying&orientation=landscape')`
        }}
      >
        {/* Animated overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-green-600/10 to-amber-600/10 animate-pulse"></div>
        
        {/* Moving background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-repeat animate-pulse" 
               style={{backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '50px 50px'}}></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6 transform hover:scale-105 transition-transform duration-300">
            Partner with Nigeria's Leading Cocoa Exporter
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Join us in building a sustainable cocoa value chain that benefits farmers, 
            communities, and global markets. Experience the quality and reliability 
            that has made us a trusted partner for over 30 years.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button variant="primary" size="lg" className="bg-amber-600 hover:bg-amber-700 transform hover:scale-105 transition-all duration-300 hover:shadow-lg">
              Get In Touch
              <div className="w-5 h-5 flex items-center justify-center ml-2">
                <i className="ri-phone-line animate-pulse"></i>
              </div>
            </Button>
            <Link to="/sustainability">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-green-800 transform hover:scale-105 transition-all duration-300">
                Our Sustainability Story
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
